import emptySliceApi from '../../store/emptyApiSlice';
import {
  ProfileAddresses,
  CartDetailsResponse,
  CartOrderDetails,
  CartPlaceOrderRequest,
  CartPlaceOrderResponse,
} from './models';
import { Address, PhoneNumber } from '../../models/ProfileModel';
import { getLocation } from '../../env-vars';
import { PhoneNumberList } from '../PhoneNumberForm/models';

export const apiSlice = emptySliceApi.injectEndpoints({
  endpoints: (builder) => ({
    getCartDetails: builder.query<CartDetailsResponse, { accountId: string }>({
      query: ({ accountId }) => `/cart/details?accountNumber=${accountId}`,
      providesTags: (result, error, arg) => [
        { type: 'CartProducts', id: arg.accountId },
      ],
    }),
    removeProduct: builder.mutation<
      void,
      { accountId: string; itemId: number }
    >({
      query: ({ accountId, itemId }) => ({
        url: `/cart/items/${itemId}?accountNumber=${accountId}`,
        method: 'DELETE',
      }),
    }),
    getProfileAddresses: builder.query<ProfileAddresses, null>({
      query: () => ({
        url: `/profile/addresses`,
      }),
      providesTags: ['Address'],
    }),
    addProfileAddress: builder.mutation<Address, Address>({
      query: (address: Address) => ({
        url: `/profile/addresses`,
        method: 'POST',
        body: address,
      }),
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return ['Address', 'CartProducts'];
      },
    }),
    updateProfileAddress: builder.mutation<Address, Address>({
      query: (address: Address) => ({
        url: `/profile/${address.id}/address`,
        method: 'PUT',
        body: address,
      }),
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return ['Address', 'CartProducts'];
      },
    }),
    setCartBillingAddress: builder.mutation<
      Address,
      { address: Address; accountId: string }
    >({
      query: ({ address, accountId }) => ({
        url: `/cart/address?accountNumber=${accountId}`,
        method: 'POST',
        body: {
          billingAddress: address,
          shippingAddress: address,
        },
      }),
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return ['CartProducts'];
      },
    }),
    getPaymentLink: builder.mutation<any, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/cart/paymentLink?accountNumber=${accountId}`,
        method: 'POST',
        body: {
          returnUrl: `${getLocation()}/hub/${accountId}/checkout-callback`,
        },
      }),
    }),
    cartPlaceOrder: builder.mutation<
      CartPlaceOrderResponse,
      { accountId: string; body: CartPlaceOrderRequest }
    >({
      query: ({ accountId, body }) => ({
        url: `/cart/order?accountNumber=${accountId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'CartProducts', id: arg.accountId },
        { type: 'Product' },
        { type: 'PhoneNumber' },
        { type: 'Address' },
      ],
    }),
    getOrderDetails: builder.query<
      CartOrderDetails,
      { accountId: string; orderId: string }
    >({
      query: ({ accountId, orderId }) => `/accounts/${accountId}/${orderId}`,
    }),
    getPhoneNumber: builder.query<PhoneNumberList, null>({
      query: () => ({
        url: `/profile/phonenumbers`,
      }),
      providesTags: ['PhoneNumber'],
    }),
    addPhoneNumber: builder.mutation<PhoneNumber, PhoneNumber>({
      query: (phoneNumber: PhoneNumber) => ({
        url: `/profile/phonenumbers`,
        method: 'POST',
        body: phoneNumber,
      }),
      invalidatesTags: (result, error) => {
        if (!error) {
          return ['PhoneNumber'];
        }
        return null;
      },
    }),
    updatePhoneNumber: builder.mutation<PhoneNumber, PhoneNumber>({
      query: (phoneNumber: PhoneNumber) => ({
        url: `/profile/phoneNumbers/${phoneNumber.id}`,
        method: 'PUT',
        body: phoneNumber,
      }),
      invalidatesTags: (result, error) => {
        if (!error) {
          return ['PhoneNumber'];
        }
        return null;
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCartDetailsQuery,
  useRemoveProductMutation,
  useGetProfileAddressesQuery,
  useAddProfileAddressMutation,
  useUpdateProfileAddressMutation,
  useSetCartBillingAddressMutation,
  useGetPaymentLinkMutation,
  useCartPlaceOrderMutation,
  useGetOrderDetailsQuery,
  useGetPhoneNumberQuery,
  useAddPhoneNumberMutation,
  useUpdatePhoneNumberMutation,
} = apiSlice;
